import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { isNullOrUndefined } from '@sla-ng/core';
import { Moment } from 'moment';
import { BehaviorSubject, filter, firstValueFrom, Observable } from 'rxjs';
import { ICalculationResult } from '../../../../models/calculation-result.model';
import { IAddCostAccounting } from '../../../../models/cost-accounting-add.model';
import { ICostAccounting } from '../../../../models/cost-accounting.model';
import { CostAccountingStateEnum } from '../../../../models/enum/cost-accounting-state.enum';
import { MaterialPriceOptionEnum } from '../../../../models/enum/material-price-option.enum';
import { ProductionTimeOptionEnum } from '../../../../models/enum/production-time-option.enum';
import { SalesPriceOptionEnum } from '../../../../models/enum/sales-price-option.enum';
import { IIndirectCostImport } from '../../../../models/indirect-cost-import.model';
import { ICostAccountingDialogLayout } from '../../../../models/layout/cost-accounting-dialog-layout.model';
import { ICostAccountingState } from '../../../../services/facades/cost-accounting/cost-accounting-state.model';
import { CostAccountingFacade } from '../../../../services/facades/cost-accounting/cost-accounting.facade';
import { CostAccountingLayoutService } from '../../../../services/layout/cost-accounting-layout.service';
import { stringToMoment } from '../../../../utils/moment-utils';
import { CostAccountingErrorDialogComponent } from './components/error-dialog/cost-accounting-error-dialog.component';
import { ImportIndirectCostComponent } from './components/import-indirect-cost/import-indirect-cost.component';

@Component({
    templateUrl: './cost-accounting-dialog.component.html',
    styleUrls: [ './cost-accounting-dialog.component.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CostAccountingDialogComponent implements OnInit, OnDestroy {

    @ViewChild(MatStepper)
    public stepper: MatStepper;

    @ViewChild(ImportIndirectCostComponent)
    public importIndirectCostComponent: ImportIndirectCostComponent;

    public stateEnum = CostAccountingStateEnum;
    public materialPriceOptionEnum = MaterialPriceOptionEnum;
    public productionTimeOptionEnum = ProductionTimeOptionEnum;
    public salesPriceOptionEnum = SalesPriceOptionEnum;

    public costAccountingState$: Observable<ICostAccountingState>;
    public selectedType: Record<string, string> = undefined;
    public layout: ICostAccountingDialogLayout;
    public costAccounting$: BehaviorSubject<ICostAccounting> = new BehaviorSubject<ICostAccounting>(undefined);
    public activeStep: number = 0;

    public dialogFormGroup = this.myFormBuilder.group(
        {
            name: [ null as string, Validators.required ],
            costAccountingType: [ null as number, Validators.required ],

            dateRange: this.myFormBuilder.group({
                start: [ null as Moment, Validators.required ],
                end: [ null as Moment, Validators.required ],
            }),

            indirectCostDateRange: this.myFormBuilder.group({
                start: [ null as Moment ],
                end: [ null as Moment ],
            }),
        },
    );

    public fallbackOptionsFormGroup = this.myFormBuilder.group({
        materialPrice: null as string,
        productionTime: null as string,
        salesPrice: null as string,
    },
    );


    public stepValidationFormGroup: FormGroup = new FormGroup({
        initializeStepControl: new FormControl(undefined, [ Validators.required ]),
        costTypeAccountingStepControl: new FormControl(undefined, [ Validators.required ]),
        costCenterAccountingStepControl: new FormControl(undefined, [ Validators.required ]),
    });
    public indirectCostOption: 'financial-accounting' | 'manual' = 'financial-accounting';

    constructor(
        private myMatDialogRef: MatDialogRef<CostAccountingDialogComponent>,
        private myCostAccountingFacade: CostAccountingFacade,
        private myCostAccountingLayoutService: CostAccountingLayoutService,
        private myMatDialog: MatDialog,
        private myFormBuilder: FormBuilder,
        @Inject(MAT_DIALOG_DATA) public data: ICostAccounting,
    ) {
    }

    public ngOnInit(): void {
        void firstValueFrom(this.myCostAccountingFacade.fetchCostAccountingTypes());

        this.layout = this.myCostAccountingLayoutService.getCostAccountingDialogLayout();
        this.costAccountingState$ = this.myCostAccountingFacade.subState();
        this.costAccounting$.next(this.data);
        this.setupValueChangeListeners();

        if (!isNullOrUndefined(this.data)) {
            void this.myCostAccountingFacade.fetchCostTypeCosts(this.data.id);
        }

        this.subscribeToCostAccounting();
    }

    public ngOnDestroy(): void {
        this.myCostAccountingFacade.clearCostTypeCosts();
    }

    public async initializeCostAccounting(): Promise<void> {
        const addCostAccounting: IAddCostAccounting = this.transformDialogData(this.dialogFormGroup.getRawValue());
        try {
            const newCostAccounting: ICostAccounting = await this.myCostAccountingFacade.initializeCostAccounting(addCostAccounting);
            this.costAccounting$.next(newCostAccounting);
        } catch (e) {
            console.error(e);
        }
    }

    public async startCostTypeAccounting(): Promise<void> {
        if (this.indirectCostOption === 'financial-accounting') {
            await this.continueCalculation();
        } else {
            await this.importCostCenterCost();
        }
        void this.myCostAccountingFacade.fetchCostTypeCosts(this.costAccounting$.value.id);
    }

    public async continueCalculation(): Promise<void> {
        try {
            const result: ICalculationResult = await this.myCostAccountingFacade.continueCostAccounting(this.costAccounting$.value.id);
            if (!isNullOrUndefined(result) && !isNullOrUndefined(result.costAccounting)) {
                this.costAccounting$.next(result.costAccounting);

                if (this.myCostAccountingFacade.hasCalculationErrors(result.errors)) {
                    this.showErrorDialog(result);
                }
            }
        } catch (e) {
            console.error(e);
        }
    }

    public close(): void {
        this.myMatDialogRef.close(false);
    }

    public clearIndirectCostDateRange(): void {
        this.dialogFormGroup.controls.indirectCostDateRange.patchValue({ start: null, end: null });
    }

    private setupValueChangeListeners(): void {

        this.dialogFormGroup.valueChanges.subscribe((data) => {
            if (data.costAccountingType != null) {
                const state: ICostAccountingState = this.myCostAccountingFacade.getStateSnapshot();
                // The following cast is needed to be able to use the KeyValue Pipe
                this.selectedType = state.costAccountingTypes.find(type => type.id === data.costAccountingType) as unknown as Record<string, string>;
            }
        });

        this.fallbackOptionsFormGroup.valueChanges.subscribe(async (data) => {
            const updatedCostAccounting = await this.myCostAccountingFacade.setFallbackOptions(this.costAccounting$.value.id, data);
            if (!isNullOrUndefined(updatedCostAccounting)) {
                this.costAccounting$.next(updatedCostAccounting);
            }
        });
    }


    private transformDialogData(data: {
        dateRange: { start: Moment, end: Moment };
        costAccountingType: number;
        name: string;
        indirectCostDateRange: { start: Moment, end: Moment };
    }): IAddCostAccounting {

        const withCustomIndirectCost = !isNullOrUndefined(data.indirectCostDateRange.start) && !isNullOrUndefined(data.indirectCostDateRange.end);

        return {
            name: data.name,
            costAccountingType: data.costAccountingType,
            from: data.dateRange?.start,
            to: data.dateRange?.end,
            indirectCost: {
                from: withCustomIndirectCost ? data.indirectCostDateRange.start : null,
                to: withCustomIndirectCost ? data.indirectCostDateRange.end : null,
            },
        };
    }

    private subscribeToCostAccounting(): void {
        this.costAccounting$.pipe(filter(c => !isNullOrUndefined(c))).subscribe((costAccounting: ICostAccounting) => {
            // Fills and disables all inputs in the initialize-step if the CostAccounting is already defined
            this.dialogFormGroup.patchValue({
                costAccountingType: costAccounting.costAccountingType.id,
                name: costAccounting.name,
            });
            this.dialogFormGroup.controls.dateRange.controls.start.patchValue(stringToMoment(costAccounting.dateRange.from));
            this.dialogFormGroup.controls.dateRange.controls.end.patchValue(stringToMoment(costAccounting.dateRange.to));

            this.dialogFormGroup.controls.indirectCostDateRange.controls.start.patchValue(stringToMoment(costAccounting.indirectCost?.from));
            this.dialogFormGroup.controls.indirectCostDateRange.controls.end.patchValue(stringToMoment(costAccounting.indirectCost?.to));

            this.dialogFormGroup.disable();

            // Set the currently active step based on the state of the CostAccounting
            switch (costAccounting.state) {
                case CostAccountingStateEnum.INITIALIZED.valueOf(): {
                    this.activeStep = 1;
                    this.stepValidationFormGroup.patchValue({ initializeStepControl: true });
                    break;
                }
                case CostAccountingStateEnum.COST_TYPE_ACCOUNTING.valueOf(): {
                    // Test: Don't change step after calculating CostTypeCost
                    this.activeStep = 2;
                    // void this.myCostAccountingFacade.fetchCostTypeCosts(costAccounting.id);
                    this.stepValidationFormGroup.patchValue({
                        initializeStepControl: true,
                        costTypeAccountingStepControl: true,
                    });
                    break;
                }
                case CostAccountingStateEnum.COST_CENTER_APPORTIONMENTS.valueOf(): {
                    this.activeStep = 3;
                    this.stepValidationFormGroup.patchValue({
                        initializeStepControl: true,
                        costTypeAccountingStepControl: true,
                        costCenterAccountingStepControl: true,
                    });
                    break;
                }
            }

            this.setFallbackOptions(costAccounting);
        },
        );
    }

    private async importCostCenterCost(): Promise<void> {
        const costCenterCost: IIndirectCostImport[] = this.importIndirectCostComponent.importFormArray.value.map(cost => ({
            costCenterId: cost.costCenterId,
            costTypeId: cost.costTypeId,
            costCt: cost.costCt * 100,
        }));
        const updatedCostAccounting: ICostAccounting = await this.myCostAccountingFacade.importIndirectCost(this.costAccounting$.value.id, costCenterCost);
        this.costAccounting$.next(updatedCostAccounting);
    }

    private setFallbackOptions(costAccounting: ICostAccounting): void {
        this.fallbackOptionsFormGroup.setValue({
            salesPrice: costAccounting.fallbackOptions.salesPrice,
            materialPrice: costAccounting.fallbackOptions.materialPrice,
            productionTime: costAccounting.fallbackOptions.productionTime,
        }, { emitEvent: false });

        if (costAccounting.state === CostAccountingStateEnum.COST_UNIT_ACCOUNTING.valueOf()) {
            this.fallbackOptionsFormGroup.disable({ emitEvent: false });
        }
    }

    private showErrorDialog(errors: ICalculationResult): void {
        this.myMatDialog.open(CostAccountingErrorDialogComponent, { data: errors });
    }
}
